// Open work (Sahil, 2026-10-13): what the Tasks & Approvals page shows and
// what the sidebar badge counts -- ONE loader, so the two cannot drift (the
// badge used to repeat the page's formula and left things out).
//
// Three kinds of thing, kept apart:
//   approvals  someone asked permission; a reviewer decides. Rows from
//              property_change_requests, doc_exception_requests and pending
//              company_members, routed to the viewer by canReviewRequest.
//   tasks      a record is missing something a person must fix. Computed
//              live by open_tasks() (migration 20261013010000) plus the
//              login-missing and move-in utility rules, grouped one card per
//              tenant or property. They vanish when the data is fixed.
//   deadlines  the lease clock (lease_clock_items): dates coming due.
import { canReviewRequest, propertyLabel, fmtDate, escapeFilterValue } from "./helpers.js";
import { REQUEST_LABELS } from "./requestLabels.js";
import { loadLoginMissingRows, buildLoginMissingTasks } from "./loginMissing.js";
import { buildMoveInUtilityTasks } from "./moveInUtilities.js";

/** The kinds open_tasks() returns, and how each reads. */
export const TASK_KINDS = {
  movein_doc: { group: "tenant", label: "Move-in paperwork", icon: "📄", waivable: "tenant" },
  movein_deposit: { group: "tenant", label: "Deposit", icon: "💵" },
  no_rent_schedule: { group: "tenant", label: "Rent schedule", icon: "🧾" },
  rent_paused: { group: "tenant", label: "Rent schedule", icon: "⏸️" },
  lease_ended: { group: "tenant", label: "Lease ended", icon: "📅", dismissable: true },
  property_record: { group: "property", label: "Property records", icon: "🏠", waivable: "property" },
  login_missing: { group: "property", label: "Portal login", icon: "🔐" },
  movein_utilities: { group: "property", label: "Utilities at move-in", icon: "💡" },
  // release 2: what used to live only on its own page
  insurance_expiring: { group: "property", label: "Insurance expiring", icon: "🛡️" },
  license_expiring: { group: "property", label: "Licence expiring", icon: "📜" },
  tax_due: { group: "property", label: "Property tax due", icon: "🏛️" },
  eviction_stage: { group: "tenant", label: "Eviction", icon: "⚖️" },
  utility_bills_review: { group: "company", label: "Utility bills to review", icon: "🧾" },
  bank_review: { group: "company", label: "Bank transactions to review", icon: "🏦" },
  je_draft: { group: "company", label: "Draft journal entries", icon: "📒" },
  housy_proposals: { group: "company", label: "Housy proposals", icon: "✨" },
};

/** Labels for the property record kinds (the third part of the item key). */
export const PROPERTY_RECORD_LABELS = { rental_license: "Rental licence", insurance: "Property insurance", property_tax: "Property tax", lead_paint: "Lead paint certificate" };

const SEV = { overdue: 0, high: 1, normal: 2 };

/**
 * Backlog (Sahil, 2026-10-21): paperwork that is old news, kept apart from
 * what is due now so it stops drowning out this week's work. A move-in
 * document for a tenancy over a year old (open_tasks words its detail
 * "Long-standing tenancy …") and a property setup record. Everything else
 * is "do now".
 */
export function isBacklog(t) {
  if (!t) return false;
  if (t.kind === "property_record") return true;
  return t.kind === "movein_doc" && String(t.detail || "").startsWith("Long-standing tenancy");
}

/** The rows of open_tasks() as task items; the login and utility rules add theirs. */
function rpcRowsToTasks(rows) {
  return (rows || []).map(r => ({
    id: r.item_key, kind: r.kind, severity: r.severity || "normal", title: r.title, detail: r.detail || "", dueDate: r.due_date || null,
    tenantId: r.tenant_id ?? null, propertyId: r.property_id ?? null, leaseId: r.lease_id ?? null, address: r.property || "",
    action: r.action || null,
    // the document type / record kind this line is about (for waiving)
    subject: r.kind === "movein_doc" ? String(r.item_key).split(":")[2] : r.kind === "property_record" ? String(r.item_key).split(":")[2] : null,
  }));
}

/** The old login-missing / move-in utility task shapes, as task items. */
function legacyToTasks(list, kind) {
  return (list || []).map((t, i) => ({
    id: kind + ":" + (t.address || "") + ":" + (t.title || i), kind, severity: t.priority === "high" ? "high" : "normal",
    title: t.title, detail: t.subtitle || "", dueDate: null, tenantId: null, propertyId: t.propertyId ?? null, leaseId: null, address: t.address || "",
    action: kind === "movein_utilities"
      ? { page: "properties", openWizardFor: { propertyId: t.propertyId, address: t.address, startAtStep: "utilities", moveInUtilities: true } }
      : t.link ? { page: t.link, ...(t.linkAction || {}) } : null,
    subject: null, legacy: t,
  }));
}

/**
 * One card per tenant (move-in paperwork, rent, lease) or per property
 * (records, logins, utilities), each holding its lines. Sorted: the most
 * urgent card first, then by address.
 */
export function groupTasks(tasks) {
  const groups = new Map();
  for (const t of tasks) {
    const g = TASK_KINDS[t.kind]?.group || "property";
    // a company-wide queue is its own one-line card
    const key = g === "company" ? "company:" + t.kind : g === "tenant" && t.tenantId != null ? "tenant:" + t.tenantId : "property:" + (t.propertyId ?? t.address);
    if (!groups.has(key)) groups.set(key, { key, group: g, tenantId: g === "tenant" ? t.tenantId : null, propertyId: t.propertyId ?? null, address: t.address || "", name: g === "company" ? t.title : g === "tenant" ? String(t.title).split(":")[0] : propertyLabel(t.address), lines: [], severity: "normal" });
    const grp = groups.get(key);
    grp.lines.push(t);
    if (SEV[t.severity] < SEV[grp.severity]) grp.severity = t.severity;
  }
  return Array.from(groups.values()).sort((a, b) => (SEV[a.severity] - SEV[b.severity]) || String(a.address).localeCompare(String(b.address)) || String(a.name).localeCompare(String(b.name)));
}

/** An approval row for the page: { id, type, icon, title, subtitle, data }. */
function approvalItem(type, r) {
  if (type === "property") {
    const tenantShaped = ["delete_tenant", "move_out", "terminate_lease", "delete_autopay", "disable_autopay", "deposit_change"].includes(r.request_type);
    // A deposit change says what it asks for ("Deposit $1,500 → $2,000: pet")
    // in its notes; the approver must see the figures before approving.
    const detail = r.request_type === "deposit_change" && r.notes ? r.notes + " · " : "";
    return { id: "prop-" + r.id, type, icon: ["delete_tenant", "move_out", "terminate_lease"].includes(r.request_type) ? "👤" : r.request_type === "archive_owner" ? "🧑‍💼" : String(r.request_type).endsWith("_autopay") ? "💳" : r.request_type === "deposit_change" ? "💰" : "🏠",
      title: (REQUEST_LABELS[r.request_type] || "Edit Property") + ": " + (tenantShaped ? (r.tenant || r.address) : r.address), subtitle: detail + "Requested by " + r.requested_by + " · " + fmtDate(r.requested_at), data: r };
  }
  if (type === "document") {
    const setup = String(r.tenant_name || "").startsWith("Setup: ");
    return { id: "doc-" + r.id, type, icon: setup ? "🏠" : "📄",
      title: setup ? "Not needed: " + (PROPERTY_RECORD_LABELS[r.doc_type] || r.doc_type) + " — " + propertyLabel(r.property) : "Waive " + (r.doc_type || "documents") + ": " + r.tenant_name,
      subtitle: (r.reason || "No reason given") + " · " + (r.requested_by || "") + " · " + fmtDate(r.created_at), data: r, setup };
  }
  return { id: "member-" + r.id, type, icon: "👤", title: "Join request: " + r.user_email, subtitle: "Role: " + (r.role || "pending") + " · " + fmtDate(r.created_at), data: r };
}

/**
 * Everything open for this viewer: { approvals, tasks, groups, deadlines,
 * counts: { approvals, tasks, deadlines, total } }.
 *   sb            the Supabase client
 *   companyId, userRole, userEmail
 *   allowedPages  the viewer's menu (login-missing rows only for pages they can open)
 *   pageAll       fetchAllPaged (accounting.js), for the paged reads
 *   withDeadlines false to skip the lease clock (the badge counts it; a caller that has it already can skip)
 */
export async function loadOpenWork(sb, { companyId, userRole, userEmail, allowedPages = null, pageAll, withDeadlines = true }) {
  const email = String(userEmail || "").toLowerCase();
  const [propReq, docReq, memReq, taskRows, props, tenants, clock] = await Promise.all([
    sb.from("property_change_requests").select("*").eq("company_id", companyId).eq("status", "pending").order("requested_at", { ascending: false }),
    sb.from("doc_exception_requests").select("*").eq("company_id", companyId).eq("status", "pending").order("created_at", { ascending: false }),
    sb.from("company_members").select("*").eq("company_id", companyId).eq("status", "pending").order("created_at", { ascending: false }),
    sb.rpc("open_tasks", { p_company_id: companyId }),
    sb.from("properties").select("id, address, status").eq("company_id", companyId).is("archived_at", null),
    sb.from("tenants").select("id, property, lease_status, move_in, lease_start, archived_at").eq("company_id", companyId).is("archived_at", null).in("lease_status", ["active", "notice"]),
    withDeadlines ? sb.rpc("lease_clock_items", { p_company_id: companyId }) : Promise.resolve({ data: [] }),
  ]);
  for (const r of [propReq, docReq, memReq, taskRows, props, clock]) if (r.error) throw r.error;

  const approvals = [];
  for (const r of propReq.data || []) if (canReviewRequest({ userRole, userEmail: email, approverEmail: r.approver_email })) approvals.push(approvalItem("property", r));
  for (const r of docReq.data || []) if (canReviewRequest({ userRole, userEmail: email, approverEmail: r.approver_email })) approvals.push(approvalItem("document", r));
  // Join requests: an admin sees all; a manager / pm sees those assigned to them.
  for (const r of memReq.data || []) {
    const mine = userRole === "admin" || (["manager", "pm"].includes(userRole) && String(r.manager_email || "").toLowerCase() === email && String(r.user_email || "").toLowerCase() !== email);
    if (mine) approvals.push(approvalItem("member", r));
  }

  const tasks = rpcRowsToTasks(taskRows.data);
  const propIdByAddr = new Map((props.data || []).map(p => [p.address, p.id]));
  const loginRows = await loadLoginMissingRows(sb, companyId, { allowedPages, pageAll });
  tasks.push(...legacyToTasks(buildLoginMissingTasks(loginRows, propIdByAddr, { allowedPages }), "login_missing"));
  const { rows: utilRows } = await pageAll(() => sb.from("utilities")
    .select("id, property, provider, responsibility, is_final_bill, movein_decided_at, archived_at")
    .eq("company_id", companyId).is("archived_at", null).order("id"), "move-in utilities");
  tasks.push(...legacyToTasks(buildMoveInUtilityTasks(utilRows || [], props.data || [], tenants.data || []), "movein_utilities"));

  const deadlines = clock.data || [];
  const groups = groupTasks(tasks);
  const pendingDocRequests = docReq.data || [];
  // A deadline names its tenant, not the property: the By property view
  // places it through this.
  const tenantProperty = Object.fromEntries((tenants.data || []).map(t => [t.id, t.property || ""]));
  return {
    approvals, tasks, groups, deadlines, pendingDocRequests, tenantProperty,
    // Tasks counts TASKS, not cards: a card (one tenant or property) holds
    // several, and counting cards meant finishing one never moved the number
    // (Sahil, 2026-10-21).
    counts: { approvals: approvals.length, tasks: tasks.length, deadlines: deadlines.length, total: approvals.length + tasks.length + deadlines.length },
  };
}

/** Dismiss a computed item for good (lease ended = "carrying on month to month"; a clock line). */
export async function dismissOpenItem(sb, companyId, itemKey, userEmail) {
  const { error } = await sb.from("lease_clock_dismissed").insert([{ company_id: companyId, item_key: itemKey, dismissed_by: userEmail || null }]);
  return { ok: !error, error };
}

/** Record that a property record is not needed (admin / owner / manager). */
export async function waivePropertyRecord(sb, companyId, propertyId, kind) {
  const { data: row, error: rErr } = await sb.from("properties").select("task_exceptions").eq("company_id", companyId).eq("id", propertyId).maybeSingle();
  if (rErr) return { ok: false, error: rErr };
  const next = Array.from(new Set([...(Array.isArray(row?.task_exceptions) ? row.task_exceptions : []), kind]));
  const { error } = await sb.from("properties").update({ task_exceptions: next }).eq("company_id", companyId).eq("id", propertyId);
  return { ok: !error, error };
}

/** Record a tenant document waiver (admin / owner); `label` is the requirement's label. */
export async function waiveTenantDoc(sb, companyId, tenantId, label) {
  const { data: row, error: rErr } = await sb.from("tenants").select("approved_doc_exceptions").eq("company_id", companyId).eq("id", tenantId).maybeSingle();
  if (rErr) return { ok: false, error: rErr };
  const next = Array.from(new Set([...(Array.isArray(row?.approved_doc_exceptions) ? row.approved_doc_exceptions : []), label]));
  const { error } = await sb.from("tenants").update({ approved_doc_exceptions: next }).eq("company_id", companyId).eq("id", tenantId);
  return { ok: !error, error };
}

/** The requester's manager, else the first active admin: who a request is routed to. */
export async function defaultApprover(sb, companyId, userEmail) {
  const { data: me } = await sb.from("app_users").select("manager_email").eq("company_id", companyId).ilike("email", escapeFilterValue(String(userEmail || "").toLowerCase())).maybeSingle();
  if (me?.manager_email) return me.manager_email;
  const { data: adm } = await sb.from("company_members").select("user_email").eq("company_id", companyId).eq("role", "admin").eq("status", "active").limit(1).maybeSingle();
  return adm?.user_email || null;
}

// ---- going somewhere from the Tasks page, and coming back -------------
//
// Every task's Open hands the page it opens `returnTo: { page: "tasks" }`;
// the page sends the person back when the thing they opened closes --
// the tenant page's Back, the wizard's Save, the policy saved, the bill
// paid (Sahil, 2026-10-16: "closing the task doesn't take me back").
// Tasks that used to land on a LIST (licence, lead, insurance, tax) now
// name the record: the property page's Licenses tab with the form open,
// or the property's section editor.
export const TASKS_RETURN = { page: "tasks" };

/** The action a task line navigates with: the stored one, pointed at the record, plus the way back. */
export function withReturn(action, task = {}) {
  if (!action?.page) return null;
  const { page, ...rest } = action;
  const out = { page, action: { ...rest, returnTo: TASKS_RETURN } };
  // The property by id as well as by address: a property whose address is
  // blank (it happens on test data) cannot be found by address.
  if (rest.openProperty !== undefined && task.propertyId != null) out.action.openPropertyId = task.propertyId;
  const subject = task.subject || "";
  if (task.kind === "property_record") {
    if (subject === "rental_license" || subject === "lead_paint") { out.page = "properties"; out.action.tab = "licenses"; out.action.openLicenseType = subject; }
    else if (subject === "insurance") { out.page = "properties"; out.action.section = "insurance"; }
    else if (subject === "property_tax") { out.page = "properties"; out.action.section = "property_tax"; }
  } else if (task.kind === "license_expiring") {
    out.action.tab = "licenses";
  }
  return out;
}

/** Back to the Tasks page when the page was opened from it; true when it went. */
export function returnFromTask(initialAction, setPage) {
  const to = initialAction?.returnTo?.page;
  if (!to || typeof setPage !== "function") return false;
  setPage(to, null);
  return true;
}
